import React from 'react';
import {
	Grid,
	TextField,
	Box,
	Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Customer } from '../../gql/graphql';
import { useShopDetails } from '../../hooks/use-shop';

interface BasicInfoTabProps {
	customerData: Partial<Customer>;
	updateCustomerData: (updates: Partial<Customer>) => void;
}

function BasicInfoTab({ customerData, updateCustomerData }: BasicInfoTabProps) {
	const { shopId, locationId } = useShopDetails();

	// Initialize shopId and locationId if not set
	React.useEffect(() => {
		if (!customerData.shopId) {
			updateCustomerData({ shopId, locationId });
		}
	}, []);

	const handleChange = (field: keyof Customer) => (
		event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		updateCustomerData({ [field]: event.target.value });
	};

	return (
		<Box>
			<Typography variant="h6" gutterBottom>
				Basic Customer Information
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
				Enter the customer's contact details
			</Typography>

			<Grid container spacing={3}>
				{/* First Name */}
				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						required
						label="First Name"
						name="firstName"
						value={customerData.firstName || ''}
						onChange={handleChange('firstName')}
						helperText="Customer's first name"
					/>
				</Grid>

				{/* Last Name */}
				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						label="Last Name"
						name="lastName"
						value={customerData.lastName || ''}
						onChange={handleChange('lastName')}
						helperText="Customer's last name"
					/>
				</Grid>

				{/* Email */}
				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						label="Email"
						name="email"
						type="email"
						value={customerData.email || ''}
						onChange={handleChange('email')}
						helperText="Customer's email address"
					/>
				</Grid>

				{/* Phone */}
				<Grid item xs={12} md={6}>
					<TextField
						fullWidth
						required
						label="Phone"
						name="phone"
						value={customerData.phone || ''}
						onChange={handleChange('phone')}
						helperText="10-digit phone number"
					/>
				</Grid>

				{/* Date of Birth */}
				<Grid item xs={12} md={6}>
					<LocalizationProvider dateAdapter={AdapterDateFns}>
						<DatePicker
							label="Date of Birth"
							value={customerData.dob ? new Date(+customerData.dob) : null}
							onChange={(value) => updateCustomerData({ dob: value?.getTime().toString() })}
						/>
					</LocalizationProvider>
				</Grid>
			</Grid>
		</Box>
	);
}

export default BasicInfoTab;
